/**
 * weekly-report-messages — keyed user-facing strings for the Weekly Status Report section.
 *
 * NOTE ON i18n: this frontend has no i18n runtime today (no next-intl / react-i18next, no
 * `locales/` catalogue — see frontend/package.json). Until one is introduced, this module is the
 * single source of truth for the section's user-facing copy so that (a) no English is hardcoded in
 * JSX and (b) renaming a label is a one-line change. Keys are namespaced the same way a real
 * catalogue would be (`weeklyReport.fields.*`), so this file can be lifted into a JSON message
 * catalogue without touching the components.
 */

export const weeklyReportMessages = {
  /** Section + sub-section headings. */
  headings: {
    section: "Weekly Report",
    reportingPeriod: "Reporting Period",
    status: "Status",
    phaseCompletion: "Phase Completion Dates",
    narrative: "Project Narrative",
    history: "Weekly Reports",
    snapshot: "Current Status",
    phaseCompletionSnapshot: "Phase Completion",
    /** V047 — the author's unsubmitted reports (visible only to them). */
    drafts: "Drafts",
  },

  /** Field titles (form labels + snapshot field labels). */
  fields: {
    // Reporting period — renamed from "From (Monday)" / "To (Friday)".
    startDate: "Start Date",
    endDate: "End Date",
    escalation: "Escalation",
    schedule: "Schedule",
    scope: "Scope",
    budget: "Budget",
    overallStatus: "Overall Project Status",
    cumulativeHours: "Cumulative Hours (to date)",
    currentPhase: "Current Phase",
    /** Suffix on a read-only phase row whose date came from an earlier weekly report. */
    phaseAlreadyRecorded: "(recorded)",
    /** A past phase with no recorded date — read-only: only the CURRENT phase is editable. */
    phaseNotRecorded: "Not recorded",
    projectHealth: "Project Health",
    plannedVsActual: "Planned vs Actual / Slippages",
    teamDidThisWeek: "What the Project Team Did This Week",
    teamWillDoNext: "What the Project Team Will Do Next",
    risks: "Risks & Blockers",
    decisions: "Decisions",
  },

  /** Snapshot (read-only) labels — shorter variants of the form field titles. */
  snapshotFields: {
    weekEnding: "Week ending",
    cumulativeHours: "Cumulative Hours:",
    projectHealth: "Project Health",
    plannedVsActual: "Planned vs Actual/Slippages",
    teamDidThisWeek: "What the Team Did This Week",
    teamWillDoNext: "What the Team Will Do Next",
    risks: "Risks & Blockers",
    decisions: "Decisions",
  },

  /** Buttons + actions. */
  actions: {
    /** Renamed from "Submit Weekly Report". */
    createReport: "Create Weekly Report",
    submitForm: "Submit report",
    /** V047 — save an incomplete report; only the end date is required. */
    saveDraft: "Save as Draft",
    /** V047 — submit button label when editing an already-submitted report. */
    resubmitForm: "Re-submit report",
    continueEditing: "Continue editing",
    edit: "Edit",
    retry: "Retry",
  },

  /** Side-panel copy (the create-report form slides in from the right). */
  panel: {
    title: "Create Weekly Report",
    description:
      "Complete the fields below and submit the weekly status report.",
    /** V047 — reopening one of your drafts. */
    draftTitle: "Continue Draft",
    draftDescription:
      "Pick up where you left off. Save as a draft again, or submit when it is complete.",
    /** V047 — editing a report that was already submitted. */
    editTitle: "Edit Weekly Report",
    editDescription:
      "Update the report and re-submit it. The original submission date is kept.",
  },

  /** V047 — draft / edit affordances. */
  lifecycle: {
    draftBadge: "Draft",
    continueEditingAria: (weekEnding: string): string =>
      `Continue editing draft for week ending ${weekEnding}`,
    editAria: (weekEnding: string): string =>
      `Edit report for week ending ${weekEnding}`,
    lastSaved: (when: string): string => `Last saved ${when}`,
    submittedBy: (author: string, when: string): string =>
      `Submitted by ${author} · ${when}`,
    lastEditedBy: (editor: string): string => `Last edited by ${editor}`,
    draftHint:
      "Only the end date is needed to save a draft. Drafts are visible only to you.",
  },

  /**
   * Overall project status indicators.
   *
   * `label` is the visible badge text; `tooltip` is the fuller meaning shown on hover/focus and
   * also used as the indicator's accessible name — the badge is a colour-coded indicator, so the
   * colour alone must never be the only carrier of meaning (WCAG 1.4.1 Use of Colour).
   *
   * Colour mapping: on_track → green, at_risk → yellow/amber, off_track → red.
   */
  overallStatus: {
    on_track: { label: "On Track", tooltip: "On Track" },
    at_risk: { label: "At Risk", tooltip: "At Risk" },
    off_track: { label: "Off Track", tooltip: "Off Track / Escalated" },
  },

  /** Placeholders. */
  placeholders: {
    select: "Select…",
    selectPhase: "Select phase…",
    cumulativeHours: "e.g. 120.5",
    projectHealth: "Overall project health summary…",
    plannedVsActual: "What slipped this week vs plan?",
    teamDidThisWeek: "Key activities and accomplishments this week…",
    teamWillDoNext: "Planned activities for next week…",
    risks: "Current risks and blockers…",
    decisions: "Decisions made this week or relevant for the short term…",
  },

  /** Helper text under a field. */
  hints: {
    /** T-30 — shown while Cumulative Hours still holds the value pre-filled from Clockify. */
    cumulativeHoursFromClockify: (syncedDate: string) =>
      `From Clockify, synced ${syncedDate}`,
  },
  /** Validation error messages (inline, next to the offending field). */
  errors: {
    endDateRequired: "End date is required.",
    cumulativeHoursInvalid: "Enter hours as a positive number, e.g. 120.5.",
    cumulativeHoursNegative: "Cumulative hours cannot be negative.",
    cumulativeHoursMax: "Cumulative hours cannot exceed 100,000.",
    phaseCompletionHint: "Only fill in if that phase actually completed",
    selectCurrentPhaseFirst:
      "Select the current phase to log its completion date.",
    /**
     * Shown (role="status") when lowering Current Phase on an existing report would hide a phase
     * date that is saved on THIS report. The date is kept in the payload — it is never blanked
     * implicitly (and the API refuses to null it without an explicit `clear_phase_dates`).
     */
    phaseDateKept: (phaseLabel: string, date: string): string =>
      `${phaseLabel} completion (${date}) is kept because it was recorded on this report.`,
  },

  /** Toast + status messages. */
  status: {
    submitSuccess: "Weekly report submitted",
    submitFailure: "Failed to submit report",
    loadFailureTitle: "Failed to load weekly reports",
    loadFailureBody: "The weekly reports could not be loaded.",
    emptyFull: "No weekly reports submitted yet.",
    emptyOverview: "No weekly report submitted yet.",
    emptyOverviewHint: "Use the button above to submit the first one.",
    draftSaved: "Draft saved",
    draftFailure: "Failed to save draft",
    resubmitSuccess: "Weekly report updated",
  },
  /** T-28: PMO review of a submitted report (leadership/admin act; everyone sees the outcome). */
  review: {
    statusLabel: {
      pending: "Awaiting PMO review",
      approved: "Approved",
      changes_requested: "Changes requested",
    },
    approve: "Approve",
    requestChanges: "Request changes",
    submitChanges: "Send request",
    cancel: "Cancel",
    noteLabel: "What needs to change?",
    notePlaceholder: "Tell the PM what to fix before the Monday PMO call",
    noteRequired: "A note is required when requesting changes.",
    reviewedBy: (who: string, when: string) => `Reviewed by ${who} on ${when}`,
    approveSuccess: "Report approved",
    changesSuccess: "Changes requested — the PM has been notified in Slack",
    failure: "Could not record the review.",
    stale:
      "This report was edited since you opened it. Reload and review the latest version.",
    notSubmitted: "Only submitted reports can be reviewed.",
  },
} as const;

export default weeklyReportMessages;
